import { ComponentType, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { GuidanceMark } from '@/platform/design-system/components/icons/GuidanceMark';
import { useReducedMotion } from '@/platform/design-system/hooks/useReducedMotion';
import type { EmptySurfaceStateKind } from './SurfaceState';

type Register = 'operational' | 'aspirational' | 'administrative' | 'analytical';

export interface PageEmptyStateAction {
  icon?: ComponentType<{ className?: string }>;
  label: string;
  description?: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
}

export interface PageEmptyStateTip {
  text: string;
}

export interface PageEmptyStateProps {
  icon: ComponentType<{ className?: string }>;
  iconColor?: string;
  title: string;
  subtitle?: string;
  register?: Register;
  actions?: PageEmptyStateAction[];
  tips?: PageEmptyStateTip[];
  eyebrow?: string;
  previewLabel?: string;
  compact?: boolean;
  className?: string;
  children?: ReactNode;
  stateKind?: EmptySurfaceStateKind;
}

/**
 * Split a chip-style class string into the swatch that belongs on the container
 * and the colour that belongs on the glyph. Background and border utilities on
 * an <svg> paint a filled box behind the icon rather than tinting it.
 */
export function splitIconClasses(iconColor?: string): {
  surface?: string;
  glyph?: string;
} {
  if (!iconColor?.trim()) return {};
  const surface: string[] = [];
  const glyph: string[] = [];
  for (const token of iconColor.split(/\s+/).filter(Boolean)) {
    // Variant-prefixed tokens (dark:, hover:) are classified on their utility.
    const utility = token.slice(token.lastIndexOf(':') + 1);
    if (utility.startsWith('bg-') || utility.startsWith('border')) {
      surface.push(token);
    } else {
      glyph.push(token);
    }
  }
  return {
    surface: surface.length ? surface.join(' ') : undefined,
    glyph: glyph.length ? glyph.join(' ') : undefined,
  };
}

function OrbWrapper({
  register,
  icon: Icon,
  iconColor,
  stateKind,
}: {
  register: Register;
  icon: ComponentType<{ className?: string }>;
  iconColor?: string;
  stateKind?: EmptySurfaceStateKind;
}) {
  // Callers pass iconColor as a single chip-style string that mixes the swatch
  // and the glyph colour ("bg-green-100 text-green-600 ..."). Applied whole to
  // an <svg> the background paints a filled rectangle behind the glyph, which
  // is the square users see. Route the surface classes to the container and
  // leave the icon with colour only.
  const { surface, glyph } = splitIconClasses(iconColor);
  const defaultIconClass = glyph ?? 'text-accent-text';

  if (register === 'operational') {
    // An operational surface pulses amber to pull the eye. That is right when
    // something is waiting on a human, and wrong when the surface is reporting
    // that nothing is: a verified-empty state is good news, so it must not
    // imitate an alert.
    const needsAttention = stateKind !== 'verified-empty' && stateKind !== 'success';
    return (
      <div className="relative flex items-center justify-center mb-5">
        {needsAttention && (
          <div
            className={clsx(
              'absolute h-20 w-20 rounded-full motion-attention-ring opacity-10 motion-reduce:animate-none',
              'bg-amber-500'
            )}
            style={{ animationDuration: '2.5s' }}
          />
        )}
        <div
          className={clsx(
            'relative flex h-16 w-16 items-center justify-center rounded-full',
            surface ??
              (needsAttention
                ? 'bg-amber-500/10 border border-amber-500/20'
                : 'bg-[rgb(var(--color-bg-tertiary))] border border-[rgb(var(--color-border-primary)/0.5)]')
          )}
        >
          <Icon
            className={clsx(
              'h-8 w-8',
              glyph ?? (needsAttention ? 'text-amber-600 dark:text-amber-400' : 'text-accent-text')
            )}
          />
        </div>
      </div>
    );
  }

  if (register === 'aspirational') {
    return (
      <div data-state-kind="verified-empty"
        className={clsx(
          'flex h-16 w-16 items-center justify-center rounded-full mb-5',
          'bg-gradient-to-br from-brand-400/20 to-brand-600/20 border border-brand-500/20',
          'empty-state-breathe motion-reduce:animate-none'
        )}
      >
        <Icon className={clsx('h-8 w-8', iconColor ?? 'text-brand-500')} />
      </div>
    );
  }

  if (register === 'analytical') {
    return (
      <div
        className={clsx(
          'flex h-16 w-16 items-center justify-center rounded-full mb-5',
          'border-2 border-dashed border-[rgb(var(--color-border-primary)/0.5)]',
          'shimmer-bg'
        )}
      >
        <Icon className={clsx('h-8 w-8', defaultIconClass)} />
      </div>
    );
  }

  // administrative (default): clean bordered square, no animation
  return (
    <div
      className={clsx(
        'flex h-16 w-16 items-center justify-center rounded-2xl mb-5',
        surface ?? 'bg-accent-muted border border-[rgb(var(--color-border-primary)/0.5)]'
      )}
    >
      <Icon className={clsx('h-8 w-8', defaultIconClass)} />
    </div>
  );
}

export function PageEmptyState({
  icon,
  iconColor,
  title,
  subtitle,
  register = 'administrative',
  actions,
  tips,
  eyebrow,
  previewLabel,
  compact = false,
  className,
  children,
  stateKind = 'verified-empty',
}: PageEmptyStateProps) {
  const reducedMotion = useReducedMotion();

  return (
    <motion.div
      initial={reducedMotion ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reducedMotion ? 0 : 0.3, ease: [0.4, 0, 0.2, 1] }}
      className={clsx(
        'relative flex flex-col items-center justify-center overflow-hidden rounded-2xl text-center',
        compact ? 'px-4 py-8' : 'px-5 py-14 sm:px-8 sm:py-16',
        className
      )}
      role="region"
      aria-label={title}
      data-state-kind={stateKind}
    >
      {/* Decorative background orb */}
      <div className="pointer-events-none absolute -top-20 right-0 h-64 w-64 rounded-full bg-[rgb(var(--color-bg-tertiary))] opacity-50 blur-3xl" />

      <OrbWrapper
        register={register}
        icon={icon}
        iconColor={iconColor}
        stateKind={stateKind}
      />

      {eyebrow && (
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.19em] text-[rgb(var(--color-text-tertiary))]">
          {eyebrow}
        </p>
      )}
      <h2 className="mb-2 text-xl font-bold tracking-tight text-[rgb(var(--color-text-primary))]">
        {title}
      </h2>
      {subtitle && (
        <p className="mb-7 max-w-lg text-sm leading-6 text-[rgb(var(--color-text-secondary))]">
          {subtitle}
        </p>
      )}

      {actions && actions.length > 0 && (
        <div className="mb-8 flex w-full max-w-xl flex-col justify-center gap-3 sm:w-auto sm:flex-row">
          {actions.map((action, i) => (
            <motion.button
              key={action.label}
              type="button"
              initial={reducedMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: reducedMotion ? 0 : 0.12 + i * 0.06,
                duration: reducedMotion ? 0 : 0.2,
              }}
              onClick={action.onClick}
              className={clsx(
                'group flex min-h-12 min-w-0 items-center gap-3 rounded-xl border px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-accent-ring focus:ring-offset-2 sm:min-w-[12rem]',
                'cursor-pointer transition-[background-color,border-color,box-shadow,transform] duration-[var(--motion-duration-feedback)] hover:-translate-y-px motion-reduce:transform-none motion-reduce:transition-none',
                (action.variant ?? (i === 0 ? 'primary' : 'secondary')) === 'primary'
                  ? 'border-transparent bg-[var(--dest-action-bg)] text-[var(--dest-action-fg)] shadow-[0_16px_30px_-22px_var(--dest-accent,var(--accent))] hover:bg-[var(--dest-action-hover)]'
                  : 'border-[rgb(var(--color-border-primary)/0.78)] bg-[rgb(var(--color-bg-secondary))] text-[rgb(var(--color-text-primary))] hover:border-[rgb(var(--color-border-secondary))] hover:bg-[rgb(var(--color-bg-tertiary))]'
              )}
            >
              {action.icon && (
                <div
                  className={clsx(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors',
                    (action.variant ?? (i === 0 ? 'primary' : 'secondary')) === 'primary'
                      ? 'bg-white/14 text-[var(--dest-action-fg)]'
                      : 'bg-[color-mix(in_srgb,var(--dest-accent,var(--accent))_12%,transparent)] text-[var(--dest-accent,var(--accent))]'
                  )}
                >
                  <action.icon className="h-4 w-4" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{action.label}</p>
                {action.description && (
                  <p
                    className={clsx(
                      'mt-0.5 text-xs leading-4',
                      (action.variant ?? (i === 0 ? 'primary' : 'secondary')) === 'primary'
                        ? 'text-white/80'
                        : 'text-[rgb(var(--color-text-tertiary))]'
                    )}
                  >
                    {action.description}
                  </p>
                )}
              </div>
              {(action.variant ?? (i === 0 ? 'primary' : 'secondary')) === 'primary' && (
                <ArrowRightIcon className="h-4 w-4 shrink-0 transition-transform duration-[var(--motion-duration-feedback)] group-hover:translate-x-0.5 motion-reduce:transform-none motion-reduce:transition-none" />
              )}
            </motion.button>
          ))}
        </div>
      )}

      {previewLabel && (
        <div className="mb-7 w-full max-w-md" aria-label={previewLabel}>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[rgb(var(--color-text-tertiary))]">
            {previewLabel}
          </p>
          <div className="relative overflow-hidden rounded-xl border border-dashed border-[rgb(var(--color-border-secondary)/0.72)] bg-[rgb(var(--color-bg-secondary)/0.48)] p-3">
            <div
              className="grid grid-cols-[1.4fr_0.8fr_0.55fr] gap-2 opacity-45"
              aria-hidden="true"
            >
              <div className="h-7 rounded-lg bg-[rgb(var(--color-bg-tertiary))]" />
              <div className="h-7 rounded-lg bg-[rgb(var(--color-bg-tertiary))]" />
              <div className="h-7 rounded-lg bg-[color-mix(in_srgb,var(--dest-accent,var(--accent))_16%,transparent)]" />
            </div>
          </div>
        </div>
      )}

      {children}

      {tips && tips.length > 0 && (
        <motion.div
          initial={reducedMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: reducedMotion ? 0 : 0.28, duration: reducedMotion ? 0 : 0.25 }}
          className="flex w-full max-w-md flex-col items-start gap-1.5 rounded-xl border border-[rgb(var(--color-border-primary)/0.5)] bg-[rgb(var(--color-bg-secondary)/0.62)] px-4 py-3.5 text-left"
        >
          <p className="mb-0.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-[rgb(var(--color-text-tertiary))]">
            <GuidanceMark className="h-3.5 w-3.5" />
            What happens next
          </p>
          {tips.map((tip, i) => (
            <p
              key={i}
              className="text-xs text-[rgb(var(--color-text-secondary))] flex items-start gap-2"
            >
              <span className="mt-1.5 h-1 w-1 rounded-full bg-brand-500/50 shrink-0" />
              {tip.text}
            </p>
          ))}
        </motion.div>
      )}
    </motion.div>
  );
}
